
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { GlobalVariables } from '@/libs/core/global';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { getFilterById, unpackSettings, getSelectedFilterLocal } from '@/libs/tpm-analytics/core/saved-filters';
import { saveChartZoom, loadChartZoom, getOptimalWindowSize, clearChartZoom } from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';
import {
    getAllProductionAnalytics,
} from '@/libs/tpm-analytics/services/production.service';
import {
    ProductionSummary,
    ProductionTrendPoint,
    ProductionByWorkplace,
    ProductionTrendByWorkplace,
    ProductionByProject,
    ProductionByShiftTeam,
    ProductionByShiftType,
    ProductionHeatmapResult,
} from '@/libs/tpm-analytics/core/types';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsProduction extends Page {
    // Loading state
    loading = false;
    filtersDebounceTimer: any = null;
    applyingInitial = false;
    
    totalOkPieces = 0;
    avgDailyProduction = 0;
    mostProductiveWeekdays: number[] = [];
    maxDailyProduction: number | null = null;
    daysAboveAverage = 0;
    totalDays = 0;
    consistencyPct: number | null = null;
    avgProductionPerShift: number | null = null;
    productionTrend: number | null = null;
    prevTotalOkPieces: number | null = null;
    prevAvgDailyProduction: number | null = null;
    
    trend: ProductionTrendPoint[] = [];
    workplaceData: ProductionByWorkplace[] = [];
    workplaceTrendData: ProductionTrendByWorkplace[] = [];
    workplaceChartMode: 'trend' | 'average' = 'trend';
    projectData: ProductionByProject[] = [];
    teamData: ProductionByShiftTeam[] = [];
    typeData: ProductionByShiftType[] = [];
    
    productionGrid: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    palette = ['#fef9c3', '#fef08a', '#fde047', '#fcd34d', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1'];
    topDays: number[] = [];
    topHours: number[] = [];
    totalProduction = 0;
    avgPerDay = 0;
    
    viewStart: number | null = null;
    viewEnd: number | null = null;
    wpViewStart: number | null = null;
    wpViewEnd: number | null = null;
    projViewStart: number | null = null;
    projViewEnd: number | null = null;
    teamViewStart: number | null = null;
    teamViewEnd: number | null = null;
    typeViewStart: number | null = null;
    typeViewEnd: number | null = null;
    
    granularity: 'day' | 'week' | 'month' = 'day';
    
    // Filter state
    periodPreset = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];

    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get currencySymbol(): string {
        const t = this.tenantId ? tenantsStore.useGetter(CrudGetter.Detail) : null;
        return (t as any)?.currency || 'CZK';
    }

    get deltaOkPiecesPct(): number {
        if (this.prevTotalOkPieces == null || this.prevTotalOkPieces === 0) return 0;
        return ((this.totalOkPieces - this.prevTotalOkPieces) / this.prevTotalOkPieces) * 100;
    }

    get deltaAvgDailyPct(): number {
        if (this.prevAvgDailyProduction == null || this.prevAvgDailyProduction === 0) return 0;
        return ((this.avgDailyProduction - this.prevAvgDailyProduction) / this.prevAvgDailyProduction) * 100;
    }

    get mostProductiveWeekdaysText(): string {
        if (this.mostProductiveWeekdays.length === 0) return '—';
        const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return this.mostProductiveWeekdays.map((d) => dayNames[d] || d).join(', ');
    }

    get daysAboveAveragePct(): number {
        if (this.totalDays === 0) return 0;
        return Math.round((this.daysAboveAverage / this.totalDays) * 100);
    }

    get topHoursText(): string {
        if (this.topHours.length === 0) return '—';
        return this.topHours.map((h) => `${h}:00`).join(', ');
    }

    get topDaysText(): string {
        if (this.topDays.length === 0) return '—';
        const dayNames = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return this.topDays.map((d) => dayNames[d] || d).join(', ');
    }

    get trendSeries(): any[] {
        if (this.trend.length === 0) return [];
        const filteredTrend = this.getVisibleData(this.trend, this.viewStart, this.viewEnd);
        return [
            {
                name: 'OK Kusy',
                data: filteredTrend.map((p) => p.okCount),
                type: 'column',
            },
            {
                name: this.$t('tpmAnalytics.movingAverage'),
                data: filteredTrend.map((p) => p.movingAvg),
                type: 'line',
            },
        ];
    }

    get trendOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        const categories = this.trend.map((d) => {
            let timestamp: number;
            
            if (this.granularity === 'week') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.granularity);
        });
        
        return {
            chart: { 
                type: 'line', 
                toolbar: { show: false }, 
                animations: { enabled: false },
                zoom: { enabled: false }
            },
            stroke: { width: [0, 2], curve: 'smooth' },
            colors: ['#0284c7', '#10b981'],
            xaxis: {
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: { 
                title: { text: 'OK Kusy' },
                labels: {
                    formatter: (val: number) => Math.round(val).toLocaleString(),
                },
            },
            legend: { position: 'top' },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                x: {
                    formatter(value: any, opts: any) {
                        if (!opts || opts.dataPointIndex == null) return value;
                        const idx = opts.dataPointIndex;
                        if (idx >= 0 && idx < self.trend.length) {
                            const d = self.trend[idx];
                            let timestamp: number;
                            
                            if (self.granularity === 'week') {
                                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
                            } else if (self.granularity === 'month') {
                                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
                            } else {
                                timestamp = moment.tz(d.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
                            }
                            
                            return self.formatAxisLabel(timestamp, self.granularity, true);
                        }
                        return value;
                    },
                },
                y: {
                    formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                },
            },
        };
    }

    get workplaceSeries(): any[] {
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: series per workplace
            if (this.workplaceTrendData.length === 0) return [];
            
            const visibleDays = this.workplaceTrendDataVisible.map(d => d.day);
            
            return this.workplaceTrendData.map((wp) => {
                const data: number[] = [];
                for (const day of visibleDays) {
                    const point = wp.points.find(p => p.day === day);
                    data.push(point ? point.okCount : 0);
                }
                
                return {
                    name: wp.workplaceName,
                    type: 'column',
                    data: data,
                };
            });
        } else {
            // Average mode: single horizontal bar series
            if (this.workplaceData.length === 0) return [];
            return [
                {
                    name: 'OK Kusy',
                    data: this.workplaceData.map((w) => ({ x: w.workplaceName, y: w.okCount })),
                },
            ];
        }
    }

    get workplaceTrendDataVisible(): ProductionTrendPoint[] {
        if (this.workplaceChartMode !== 'trend') return [];
        
        // Spojíme všechny points z workplaceTrendData do jednoho pole dnů
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();
        
        if (this.wpViewStart == null || this.wpViewEnd == null) {
            return sortedDays.map(day => ({ day, okCount: 0, movingAvg: null }));
        }
        
        const start = Math.max(0, Math.min(sortedDays.length - 1, this.wpViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.wpViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, okCount: 0, movingAvg: null }));
    }

    get workplaceChartHeight(): number {
        if (this.workplaceChartMode === 'trend') {
            return 520; // Fixní výška pro trend režim
        }
        const minBarHeight = 30;
        const maxBarHeight = 60;
        const spacing = 10;
        const baseHeight = 120; // Pro osy, legendu a spacing
        
        // Výpočet skutečné výšky baru
        const calculatedBarHeight = Math.floor(420 / this.workplaceData.length) - 10;
        const actualBarHeight = Math.min(maxBarHeight, Math.max(minBarHeight, calculatedBarHeight));
        
        // Celková výška = počet barů * (výška baru + spacing) + základní výška
        const calculatedHeight = this.workplaceData.length * (actualBarHeight + spacing) + baseHeight;
        return Math.max(300, calculatedHeight);
    }

    get workplaceOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;
        
        if (this.workplaceChartMode === 'trend') {
            // Trend mode: X = time periods, Y = OK kusy for each workplace
            const categories = this.workplaceTrendDataVisible.map((d) => {
                const m = moment(d.day);
                if (this.granularity === 'day') {
                    return m.format('D.M.');
                } else if (this.granularity === 'week') {
                    const start = m.clone().startOf('isoWeek');
                    const end = m.clone().endOf('isoWeek');
                    const week = m.isoWeek();
                    const weekLabel = `${week}. týden`;
                    const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
                    return weekLabel + '|' + dateRange;
                } else {
                    return m.format('MM/YYYY');
                }
            });
            
            const shouldRotate = this.granularity === 'week' || categories.length > 8;
            
            return {
                chart: {
                    id: 'production-workplace-trend',
                    type: 'bar',
                    stacked: false,
                    toolbar: { show: false },
                    zoom: { enabled: false },
                    animations: { enabled: false },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '80%',
                        dataLabels: {
                            position: 'top',
                        },
                    },
                },
                xaxis: {
                    categories,
                    labels: {
                        rotate: shouldRotate ? -45 : 0,
                        rotateAlways: shouldRotate,
                        hideOverlappingLabels: true,
                        trim: true,
                        minHeight: shouldRotate ? 60 : undefined,
                        maxHeight: shouldRotate ? 80 : undefined,
                        style: {
                            cssClass: 'apexcharts-xaxis-label-multiline',
                        },
                        formatter(value: any) {
                            if (typeof value === 'string' && value.includes('|')) {
                                const parts = value.split('|');
                                return parts;
                            }
                            return value;
                        },
                    },
                },
                yaxis: {
                    title: { text: 'OK Kusy' },
                    labels: {
                        formatter: (val: number) => Math.round(val).toLocaleString(),
                    },
                },
                dataLabels: { enabled: false },
                legend: { position: 'top' },
                tooltip: {
                    shared: true,
                    intersect: false,
                    followCursor: true,
                    y: {
                        formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                    },
                },
                colors: ['#0284c7', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'],
            } as any;
        } else {
            // Average mode: horizontal bar chart
            const calculatedBarHeight = Math.floor(this.workplaceChartHeight / this.workplaceData.length) - 10;
            const barHeight = Math.min(60, Math.max(30, calculatedBarHeight)); // Max 60px, min 30px
            return {
                chart: { 
                    type: 'bar', 
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                plotOptions: { 
                    bar: { 
                        horizontal: true,
                        barHeight: barHeight + 'px',
                    } 
                },
                colors: ['#0284c7'],
                xaxis: { 
                    categories: this.workplaceData.map((w) => w.workplaceName),
                    labels: {
                        formatter: (val: number) => Math.round(val).toLocaleString(),
                    },
                },
                yaxis: { title: { text: 'OK Kusy' } },
                dataLabels: { 
                    enabled: true,
                    formatter: (val: number) => Math.round(val).toLocaleString(),
                },
                tooltip: { 
                    shared: true, 
                    intersect: false,
                    followCursor: true,
                    y: {
                        formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                    },
                },
            };
        }
    }

    get projectTrendDataVisible(): ProductionTrendPoint[] {
        // Spojíme všechny points z projectData do jednoho pole dnů/týdnů
        const allDays = new Set<string>();
        for (const project of this.projectData) {
            for (const pt of project.points) {
                allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();

        if (this.projViewStart == null || this.projViewEnd == null) {
            return sortedDays.map(day => ({ day, okCount: 0, movingAvg: null }));
        }

        const start = Math.max(0, Math.min(sortedDays.length - 1, this.projViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.projViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, okCount: 0, movingAvg: null }));
    }

    get projectSeries(): any[] {
        if (this.projectData.length === 0) return [];

        const visibleDays = this.projectTrendDataVisible.map(d => d.day);

        return this.projectData.map((project) => {
            const data: number[] = [];
            for (const day of visibleDays) {
                const point = project.points.find(p => p.day === day);
                data.push(point ? point.okCount : 0);
            }

            return {
                name: project.projectName,
                data: data,
            };
        });
    }

    get projectOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const categories: string[] = [];
        const visiblePoints = this.projectTrendDataVisible;

        for (const point of visiblePoints) {
            let timestamp: number;

            if (this.granularity === 'week') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }

            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
        
        return {
            chart: { 
                type: 'bar', 
                stacked: false, 
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: { 
                title: { text: 'OK Kusy' },
                labels: {
                    formatter: (val: number) => Math.round(val).toLocaleString(),
                },
            },
            dataLabels: { enabled: false },
            legend: { position: 'top' },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                },
            },
        };
    }

    get teamTrendDataVisible(): ProductionTrendPoint[] {
        // Spojíme všechny points z teamData do jednoho pole dnů/týdnů
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            for (const pt of team.points) {
                allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();

        if (this.teamViewStart == null || this.teamViewEnd == null) {
            return sortedDays.map(day => ({ day, okCount: 0, movingAvg: null }));
        }

        const start = Math.max(0, Math.min(sortedDays.length - 1, this.teamViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.teamViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, okCount: 0, movingAvg: null }));
    }

    get teamSeries(): any[] {
        if (this.teamData.length === 0) return [];

        const visibleDays = this.teamTrendDataVisible.map(d => d.day);

        return this.teamData.map((team) => {
            const data: number[] = [];
            for (const day of visibleDays) {
                const point = team.points.find(p => p.day === day);
                data.push(point ? point.okCount : 0);
            }

            return {
                name: team.shiftTeamTypeName,
                data: data,
            };
        });
    }

    get teamOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const categories: string[] = [];
        const visiblePoints = this.teamTrendDataVisible;

        for (const point of visiblePoints) {
            let timestamp: number;

            if (this.granularity === 'week') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }

            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
        
        return {
            chart: { 
                type: 'bar', 
                stacked: false, 
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: { 
                title: { text: 'OK Kusy' },
                labels: {
                    formatter: (val: number) => Math.round(val).toLocaleString(),
                },
            },
            dataLabels: { enabled: false },
            legend: { position: 'top' },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                },
            },
        };
    }

    get typeTrendDataVisible(): ProductionTrendPoint[] {
        // Spojíme všechny points z typeData do jednoho pole dnů/týdnů
        const allDays = new Set<string>();
        for (const type of this.typeData) {
            for (const pt of type.points) {
                allDays.add(pt.day);
            }
        }
        const sortedDays = Array.from(allDays).sort();

        if (this.typeViewStart == null || this.typeViewEnd == null) {
            return sortedDays.map(day => ({ day, okCount: 0, movingAvg: null }));
        }

        const start = Math.max(0, Math.min(sortedDays.length - 1, this.typeViewStart));
        const end = Math.max(start, Math.min(sortedDays.length - 1, this.typeViewEnd));
        return sortedDays.slice(start, end + 1).map(day => ({ day, okCount: 0, movingAvg: null }));
    }

    get typeSeries(): any[] {
        if (this.typeData.length === 0) return [];

        const visibleDays = this.typeTrendDataVisible.map(d => d.day);

        return this.typeData.map((type) => {
            const data: number[] = [];
            for (const day of visibleDays) {
                const point = type.points.find(p => p.day === day);
                data.push(point ? point.okCount : 0);
            }

            return {
                name: type.shiftTypeName,
                data: data,
            };
        });
    }

    get typeOptions(): any {
        const self = this;
        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const categories: string[] = [];
        const visiblePoints = this.typeTrendDataVisible;

        for (const point of visiblePoints) {
            let timestamp: number;

            if (this.granularity === 'week') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.granularity === 'month') {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment.tz(point.day, DISPLAY_TIMEZONE).startOf('day').valueOf() + dayStartMs;
            }

            categories.push(self.formatAxisLabel(timestamp, self.granularity));
        }
        
        return {
            chart: { 
                type: 'bar', 
                stacked: false, 
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            xaxis: { 
                type: 'category',
                categories: categories,
                labels: {
                    rotate: self.granularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        if (typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                },
            },
            yaxis: { 
                title: { text: 'OK Kusy' },
                labels: {
                    formatter: (val: number) => Math.round(val).toLocaleString(),
                },
            },
            dataLabels: { enabled: false },
            legend: { position: 'top' },
            tooltip: { 
                shared: true, 
                intersect: false, 
                followCursor: true,
                y: {
                    formatter: (val: number) => val != null ? Math.round(val).toLocaleString() : '—',
                },
            },
        };
    }

    formatPieces(value: number | null | undefined): string {
        if (value == null) return '—';
        return Math.round(value).toLocaleString();
    }

    formatDelta(pct: number): string {
        const sign = pct > 0 ? '+' : '';
        return `${sign}${pct.toFixed(1)}%`;
    }

    formatPct(value: number | null | undefined): string {
        if (value == null) return '—';
        return value.toFixed(1) + '%';
    }

    formatTrend(value: number | null | undefined): string {
        if (value == null) return '—';
        const sign = value > 0 ? '+' : '';
        return `${sign}${value.toFixed(1)}%`;
    }

    getTrendClass(value: number | null | undefined): string {
        if (value == null) return '';
        if (value > 0) return 'text-success';
        if (value < 0) return 'text-danger';
        return '';
    }

    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month', forTooltip = false): string {
        const m = moment.tz(ts, DISPLAY_TIMEZONE);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            
            if (forTooltip) {
                return `${weekLabel}<br/>${dateRange}`;
            }
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }

    dayName(index: number): string {
        const names = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
        return names[index] || '';
    }

    cellColor(value: number): string {
        if (value === 0) return this.palette[0];
        const max = Math.max(...this.productionGrid.flat());
        const ratio = max > 0 ? value / max : 0;
        
        // 9 úrovní pro plynulejší přechod
        if (ratio < 0.05) return this.palette[0];
        if (ratio < 0.15) return this.palette[1];
        if (ratio < 0.25) return this.palette[2];
        if (ratio < 0.35) return this.palette[3];
        if (ratio < 0.50) return this.palette[4];
        if (ratio < 0.65) return this.palette[5];
        if (ratio < 0.80) return this.palette[6];
        if (ratio < 0.92) return this.palette[7];
        return this.palette[8];
    }

    getCurrentFilterState(): any {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.granularity,
            workplaceChartMode: this.workplaceChartMode,
        };
    }

    applyFilterState(state: any, triggerLoad = true): void {
        console.log('[Production] applyFilterState called:', {
            triggerLoad,
            applyingInitial: this.applyingInitial,
            state: { ...state },
        });
        
        this.periodPreset = state.periodPreset || 'last7d';
        this.customFrom = state.customFrom || null;
        this.customTo = state.customTo || null;
        this.selectedLocationIds = state.selectedLocationIds || [];
        this.selectedWorkplaceIds = state.selectedWorkplaceIds || [];
        this.selectedProjectIds = state.selectedProjectIds || [];
        this.selectedShiftTypeIds = state.selectedShiftTypeIds || [];
        this.selectedShiftTeamTypeIds = state.selectedShiftTeamTypeIds || [];
        this.granularity = state.granularity || 'day';
        if (state?.workplaceChartMode) {
            this.workplaceChartMode = state.workplaceChartMode;
        }
        
        if (triggerLoad) {
            console.log('[Production] Calling onFiltersChanged()');
            this.onFiltersChanged();
        } else {
            console.log('[Production] Skipping load (triggerLoad=false)');
        }
    }

    onFiltersChanged(): void {
        console.log('[Production] onFiltersChanged called:', {
            applyingInitial: this.applyingInitial,
        });
        
        if (this.applyingInitial) {
            console.log('[Production] Skipping onFiltersChanged (applyingInitial=true)');
            return;
        }
        
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            console.log('[Production] Debounce timeout - calling load()');
            this.load();
            const fn = (this.$root as any).$tpmOnStateChanged;
            if (typeof fn === 'function') fn();
        }, 200);
    }

    async load(): Promise<void> {
        // Zabránění paralelním načítáním
        if (this.loading) {
            return;
        }
        
        this.loading = true;
        
        try {
        const range = getDateRange({
            periodPreset: this.periodPreset as any,
            customFrom: this.customFrom,
            customTo: this.customTo,
        });
        const filters = {
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        };

        // OPTIMALIZACE: Pouze 1 volání místo 7 - načte směny jen 2x (current + previous)
        const analytics = await getAllProductionAnalytics(range.from, range.to, this.granularity, filters);

        // Rozbalení výsledků
        this.totalOkPieces = analytics.summary.totalOkPieces;
        this.avgDailyProduction = analytics.summary.avgDailyProduction;
        this.mostProductiveWeekdays = analytics.summary.mostProductiveWeekdays;
        this.maxDailyProduction = analytics.summary.maxDailyProduction;
        this.daysAboveAverage = analytics.summary.daysAboveAverage;
        this.totalDays = analytics.summary.totalDays;
        this.consistencyPct = analytics.summary.consistencyPct;
        this.avgProductionPerShift = analytics.summary.avgProductionPerShift;
        this.productionTrend = analytics.summary.productionTrend;
        this.prevTotalOkPieces = analytics.summary.prev?.totalOkPieces ?? null;
        this.prevAvgDailyProduction = analytics.summary.prev?.avgDailyProduction ?? null;

        this.trend = analytics.trend;
        this.workplaceData = analytics.byWorkplace;
        this.workplaceTrendData = analytics.workplaceTrend;
        this.projectData = analytics.byProject;
        this.teamData = analytics.byShiftTeam;
        this.typeData = analytics.byShiftType;

        this.productionGrid = analytics.heatmap.grid;
        this.topDays = analytics.heatmap.topDayIndexes;
        this.topHours = analytics.heatmap.topHours;
        this.totalProduction = analytics.heatmap.totalProduction;
        this.avgPerDay = analytics.heatmap.avgPerDay;

        this.initZoom();
        } catch (error) {
            console.error('[Production] Error loading data:', error);
        } finally {
            this.loading = false;
        }
    }

    initZoom(): void {
        const optimal = getOptimalWindowSize(this.granularity);
        this.viewStart = Math.max(0, this.trend.length - optimal);
        this.viewEnd = this.trend.length;
        
        // Initialize workplace trend zoom
        const wpAllDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                wpAllDays.add(pt.day);
            }
        }
        const wpTotalDays = wpAllDays.size;
        this.wpViewStart = 0;
        this.wpViewEnd = Math.min(wpTotalDays - 1, optimal - 1);
        
        // Initialize project zoom - použít všechny unikátní dny
        const projAllDays = new Set<string>();
        for (const proj of this.projectData) {
            for (const pt of proj.points) {
                projAllDays.add(pt.day);
            }
        }
        const projTotalDays = projAllDays.size;
        this.projViewStart = 0;
        this.projViewEnd = Math.min(projTotalDays - 1, optimal - 1);
        
        // Initialize team zoom - použít všechny unikátní dny
        const teamAllDays = new Set<string>();
        for (const team of this.teamData) {
            for (const pt of team.points) {
                teamAllDays.add(pt.day);
            }
        }
        const teamTotalDays = teamAllDays.size;
        this.teamViewStart = 0;
        this.teamViewEnd = Math.min(teamTotalDays - 1, optimal - 1);
        
        // Initialize type zoom - použít všechny unikátní dny
        const typeAllDays = new Set<string>();
        for (const type of this.typeData) {
            for (const pt of type.points) {
                typeAllDays.add(pt.day);
            }
        }
        const typeTotalDays = typeAllDays.size;
        this.typeViewStart = 0;
        this.typeViewEnd = Math.min(typeTotalDays - 1, optimal - 1);
    }

    getVisibleData<T>(data: T[], start: number | null, end: number | null): T[] {
        if (start == null || end == null) return data;
        return data.slice(start, end);
    }

    zoomIn(): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        const mid = Math.floor((this.viewStart + this.viewEnd) / 2);
        this.viewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.viewEnd = Math.min(this.trend.length, this.viewStart + newRange);
    }

    zoomOut(): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const newRange = Math.min(this.trend.length, Math.round(range * 1.25));
        const mid = Math.floor((this.viewStart + this.viewEnd) / 2);
        this.viewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.viewEnd = Math.min(this.trend.length, this.viewStart + newRange);
    }

    shiftPercent(pct: number): void {
        if (this.viewStart == null || this.viewEnd == null) return;
        const range = this.viewEnd - this.viewStart;
        const shift = Math.round(range * pct);
        this.viewStart = Math.max(0, Math.min(this.trend.length - range, this.viewStart + shift));
        this.viewEnd = this.viewStart + range;
    }

    wpZoomIn(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const range = this.wpViewEnd - this.wpViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));
        
        // Get total number of unique days from trend data
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen - 1, this.wpViewStart + newRange - 1);
    }

    wpZoomOut(): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const range = this.wpViewEnd - this.wpViewStart;
        
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.wpViewStart + this.wpViewEnd) / 2);
        this.wpViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.wpViewEnd = Math.min(maxLen - 1, this.wpViewStart + newRange - 1);
    }

    wpShiftPercent(pct: number): void {
        if (this.wpViewStart == null || this.wpViewEnd == null) return;
        const range = this.wpViewEnd - this.wpViewStart + 1;
        
        const allDays = new Set<string>();
        for (const wp of this.workplaceTrendData) {
            for (const pt of wp.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;
        
        const shift = Math.round(range * pct);
        this.wpViewStart = Math.max(0, Math.min(maxLen - range, this.wpViewStart + shift));
        this.wpViewEnd = this.wpViewStart + range - 1;
    }

    projZoomIn(): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));

        const allDays = new Set<string>();
        for (const project of this.projectData) {
            for (const pt of project.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const mid = Math.floor((this.projViewStart + this.projViewEnd) / 2);
        this.projViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.projViewEnd = Math.min(maxLen - 1, this.projViewStart + newRange - 1);
    }

    projZoomOut(): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart;

        const allDays = new Set<string>();
        for (const project of this.projectData) {
            for (const pt of project.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.projViewStart + this.projViewEnd) / 2);
        this.projViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.projViewEnd = Math.min(maxLen - 1, this.projViewStart + newRange - 1);
    }

    projShiftPercent(pct: number): void {
        if (this.projViewStart == null || this.projViewEnd == null) return;
        const range = this.projViewEnd - this.projViewStart + 1;

        const allDays = new Set<string>();
        for (const project of this.projectData) {
            for (const pt of project.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const shift = Math.round(range * pct);
        this.projViewStart = Math.max(0, Math.min(maxLen - range, this.projViewStart + shift));
        this.projViewEnd = this.projViewStart + range - 1;
    }

    teamZoomIn(): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));

        // Počet unikátních dnů ze všech týmů
        const allDays = new Set<string>();
        for (const team of this.teamData) {
            for (const pt of team.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const mid = Math.floor((this.teamViewStart + this.teamViewEnd) / 2);
        this.teamViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.teamViewEnd = Math.min(maxLen - 1, this.teamViewStart + newRange - 1);
    }

    teamZoomOut(): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart;

        const allDays = new Set<string>();
        for (const team of this.teamData) {
            for (const pt of team.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.teamViewStart + this.teamViewEnd) / 2);
        this.teamViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.teamViewEnd = Math.min(maxLen - 1, this.teamViewStart + newRange - 1);
    }

    teamShiftPercent(pct: number): void {
        if (this.teamViewStart == null || this.teamViewEnd == null) return;
        const range = this.teamViewEnd - this.teamViewStart + 1;

        const allDays = new Set<string>();
        for (const team of this.teamData) {
            for (const pt of team.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const shift = Math.round(range * pct);
        this.teamViewStart = Math.max(0, Math.min(maxLen - range, this.teamViewStart + shift));
        this.teamViewEnd = this.teamViewStart + range - 1;
    }

    typeZoomIn(): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart;
        const newRange = Math.max(7, Math.round(range * 0.75));

        const allDays = new Set<string>();
        for (const type of this.typeData) {
            for (const pt of type.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const mid = Math.floor((this.typeViewStart + this.typeViewEnd) / 2);
        this.typeViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.typeViewEnd = Math.min(maxLen - 1, this.typeViewStart + newRange - 1);
    }

    typeZoomOut(): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart;

        const allDays = new Set<string>();
        for (const type of this.typeData) {
            for (const pt of type.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const newRange = Math.min(maxLen, Math.round(range * 1.25));
        const mid = Math.floor((this.typeViewStart + this.typeViewEnd) / 2);
        this.typeViewStart = Math.max(0, mid - Math.floor(newRange / 2));
        this.typeViewEnd = Math.min(maxLen - 1, this.typeViewStart + newRange - 1);
    }

    typeShiftPercent(pct: number): void {
        if (this.typeViewStart == null || this.typeViewEnd == null) return;
        const range = this.typeViewEnd - this.typeViewStart + 1;

        const allDays = new Set<string>();
        for (const type of this.typeData) {
            for (const pt of type.points) {
                allDays.add(pt.day);
            }
        }
        const maxLen = allDays.size;

        const shift = Math.round(range * pct);
        this.typeViewStart = Math.max(0, Math.min(maxLen - range, this.typeViewStart + shift));
        this.typeViewEnd = this.typeViewStart + range - 1;
    }

    downloadChart(name: string): void {
        console.log(`Download chart: ${name}`);
    }

    $watch!: any;
    
    created(): void {
        console.log('[Production] created() called');
        // Registrace rozhraní pro parent a načtení stavu z aktuálně vybraného filtru
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        
        this.applyingInitial = true;
        
        const initState = (this.$root as any).$tpmInitialState || {};
        const initSid = (this.$root as any).$tpmInitialSid || null;
        
        console.log('[Production] created() - initState:', initState, 'initSid:', initSid);
        
        if (initSid) {
            console.log('[Production] Loading filter from initSid:', initSid);
            getFilterById(initSid).then((model) => {
                console.log('[Production] Filter loaded:', model);
                if (model) {
                    const st = unpackSettings(model.settings);
                    this.applyFilterState(st, false);
                }
                this.applyingInitial = false;
                // Po načtení filtru explicitně zavoláme load
                this.onFiltersChanged();
            }).catch((err) => {
                console.error('[Production] Error loading filter:', err);
                this.applyingInitial = false;
                this.onFiltersChanged();
            });
        } else if (Object.keys(initState).length > 0) {
            console.log('[Production] Applying initState');
            this.applyFilterState(initState, false);
            this.applyingInitial = false;
        } else {
            console.log('[Production] No initial state - mounted() will call load()');
            // Žádný initial state - mounted() zavolá load()
            this.applyingInitial = false;
        }
    }
    
    mounted(): void {
        console.log('[Production] mounted() called, applyingInitial:', this.applyingInitial);
        // V mounted() voláme load() pouze pokud už není applyingInitial
        // (pokud je, created() ho zavolá až se načte filtr)
        if (!this.applyingInitial) {
            console.log('[Production] mounted() - calling onFiltersChanged()');
            this.onFiltersChanged();
        } else {
            console.log('[Production] mounted() - skipping onFiltersChanged (applyingInitial=true)');
        }
        
        // Watch na granularitu - když se změní, znovu načteme data
        this.$watch('granularity', () => {
            this.onFiltersChanged();
        });
        
        // Watch na workplaceChartMode - když se změní, notifikujeme parent
        this.$watch('workplaceChartMode', (newVal: string, oldVal: string) => {
            if (oldVal && newVal !== oldVal && !this.applyingInitial) {
                const currentState = this.getCurrentFilterState();
                if ((this.$root as any).$tpmOnStateChanged) {
                    (this.$root as any).$tpmOnStateChanged(currentState);
                }
            }
        });
    }

    destroyed(): void {
        // Cleanup debounce timer
        if (this.filtersDebounceTimer) {
            clearTimeout(this.filtersDebounceTimer);
        }
        
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState) {
            delete (this.$root as any).$tpmGetCurrentState;
        }
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) {
            delete (this.$root as any).$tpmApplyState;
        }
    }
}
